import { API_BASE } from '../env'
import type { DeviceTypeWire } from './devices'
import { request } from './http'
import type { AdminMessageWire } from './users'
import type { Schema } from './schema'

// ── Administration: brands, locations, device types and devices ──────────────────────────────────
//
// All four list endpoints answer with an envelope (`{ brands }`, `{ locations }`, `{ device_types }`,
// and the paged `{ devices, total, page, limit, has_more }`), unlike the permission endpoints above.
//
// **The device list's query vocabulary is not the body vocabulary.** It reads `brand`, `location`
// and `device_type` — not the `brand_id`/`location_id`/`device_type_id` every request DTO uses. The
// route ratchet checks paths, not query strings, so a wrong name here would pass every check and
// silently return an unfiltered page; it is taken from `DeviceFilter.FromQueryString`.

export type AdminLocationWire = Schema<'Location'>

/** A row of the paged admin device list. Names arrive resolved; nothing needs joining client-side. */
export type AdminDeviceRowWire = Schema<'AdminDeviceRowWire'>

export type AdminDevicePageWire = Schema<'AdminDevicePageWire'>

/** The filter the server actually parses. See the note above about the parameter names. */
export interface AdminDeviceQuery {
  brand?: string
  location?: string
  deviceType?: string
  search?: string
  enabled?: boolean
  online?: boolean
  /** Capped at 200 server-side; defaults to 50. */
  limit?: number
  offset?: number
}

export function adminCreateBrand(
  name: string,
  description: string | null,
): Promise<Schema<'AdminBrandCreatedWire'>> {
  return request<Schema<'AdminBrandCreatedWire'>>(
    'POST',
    `${API_BASE}/admin/brands`,
    { json: { name, description } },
  )
}

/**
 * Updates a brand.
 *
 * `is_enabled` is always sent. The server applies `body.IsEnabled ?? true`, and deleting a brand is
 * a *soft* delete (`UPDATE brands SET is_enabled = FALSE`) — so omitting the field, as the Blazor
 * client does, silently resurrects a deleted brand the moment anyone edits its name.
 */
export function adminUpdateBrand(
  brandId: string,
  body: {
    name: string
    description: string | null
    is_enabled: boolean
    /** Enrolling with no claim, on a pre-registration alone. Omitted leaves the brand as it was. */
    claimless_enrollment?: boolean
    /** CIDRs allowed to enroll with no claim; an empty array removes the restriction. */
    claimless_ip_allow?: string[]
    /**
     * Days a browser that gave the authenticator code is remembered (0 to 30); null follows the global setting;
     * omitted leaves it (Plans/sign-in-remembered-browsers.md).
     */
    remember_browser_days?: number | null
    /** Everyone who reaches the brand gives a code at sign-in. Omitted leaves it as it was. */
    require_second_factor?: boolean
    /** A fresh code before remote control of the brand's devices. Omitted leaves it as it was. */
    remote_control_check?: boolean
  },
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('PUT', `${API_BASE}/admin/brands/${brandId}`, { json: body })
}

export function adminDeleteBrand(brandId: string): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/brands/${brandId}`)
}

export function adminGetLocations(brand?: string): Promise<Schema<'AdminLocationListWire'>> {
  const params = new URLSearchParams()
  if (brand) params.set('brand', brand)
  const query = params.toString()
  return request<Schema<'AdminLocationListWire'>>(
    'GET',
    `${API_BASE}/admin/locations?${query}`,
  )
}

export function adminCreateLocation(body: {
  brand_id: string
  name: string
  store_number: string | null
  city: string | null
  state: string | null
  latitude: number | null
  longitude: number | null
  /** IANA id; omitted, the server's default. An unknown one is refused with a 400. */
  timezone?: string
  store_type_id?: string
}): Promise<Schema<'AdminLocationCreatedWire'>> {
  return request<Schema<'AdminLocationCreatedWire'>>('POST', `${API_BASE}/admin/locations`, { json: body })
}

/**
 * Updates a location.
 *
 * Nine fields, all of which the handler applies. The Blazor client sends seven, so address and
 * postal code are unsettable from that page and are wiped by the create path, which creates and then
 * immediately updates with `null` for both.
 *
 * `timezone` and `store_type_id` are left unchanged when omitted; `store_type_id: ""` removes the
 * store type.
 */
export function adminUpdateLocation(
  locationId: string,
  body: {
    name: string
    store_number: string | null
    address: string | null
    city: string | null
    state: string | null
    postal_code: string | null
    latitude: number | null
    longitude: number | null
    is_enabled: boolean
    timezone?: string
    store_type_id?: string
  },
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('PUT', `${API_BASE}/admin/locations/${locationId}`, {
    json: body,
  })
}

export function adminDeleteLocation(locationId: string): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/locations/${locationId}`)
}

// The list is fetched by `adminGetDeviceTypes`, declared once with the device-details endpoints.
// A second getter for the same arm would be a near-duplicate with nothing to keep the two in step.

export function adminCreateDeviceType(body: {
  name: string
  description: string | null
  icon: string | null
}): Promise<DeviceTypeWire> {
  return request<DeviceTypeWire>('POST', `${API_BASE}/admin/device-types`, { json: body })
}

export function adminUpdateDeviceType(
  deviceTypeId: string,
  body: { name: string; description: string | null; icon: string | null },
): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('PUT', `${API_BASE}/admin/device-types/${deviceTypeId}`, {
    json: body,
  })
}

export function adminDeleteDeviceType(deviceTypeId: string): Promise<AdminMessageWire> {
  return request<AdminMessageWire>('DELETE', `${API_BASE}/admin/device-types/${deviceTypeId}`)
}

export function adminGetDevices(query: AdminDeviceQuery = {}): Promise<AdminDevicePageWire> {
  const params = new URLSearchParams()
  if (query.brand) params.set('brand', query.brand)
  if (query.location) params.set('location', query.location)
  if (query.deviceType) params.set('device_type', query.deviceType)
  if (query.search) params.set('search', query.search)
  if (query.enabled != null) params.set('enabled', String(query.enabled))
  if (query.online != null) params.set('online', String(query.online))
  if (query.limit != null) params.set('limit', String(query.limit))
  if (query.offset != null) params.set('offset', String(query.offset))
  const search = params.toString()
  return request<AdminDevicePageWire>('GET', `${API_BASE}/admin/devices?${search}`)
}

/** Answers with the enrollment token, which is shown once and never returned again. */
export function adminCreateDevice(body: {
  location_id: string
  device_id: string
  device_type_id: string
  name: string | null
  description: string | null
}): Promise<Schema<'AdminDeviceCreatedWire'>> {
  return request<Schema<'AdminDeviceCreatedWire'>>(
    'POST',
    `${API_BASE}/admin/devices`,
    { json: body },
  )
}

// `adminUpdateDevice` and `adminDeleteDevice` are declared once, with the device-details endpoints
// above. Both pages share them; see `AdminDeviceUpdateWire` for why `is_enabled` is not nullable.

// ── Store types ──────────────────────────────────────────────────────────────────────────────────
//
// A location attribute (drive-thru, mall, …) that smart groups select on. Lives under
// `/api/admin/`, which the server gates on `system.admin` as a whole, so callers treat a refusal as
// "no list available" rather than as an error.

export type StoreTypeWire = Schema<'StoreType'>

export function getStoreTypes(): Promise<Schema<'StoreTypeListWire'>> {
  return request<Schema<'StoreTypeListWire'>>('GET', `${API_BASE}/admin/store-types`)
}
